import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import * as DATE_PERIOD from "constants/dateFilter";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useMemo, useState } from "react";
import {
  useRefreshReceiptsMutation,
  useRefreshTransactionsMutation,
  useGetAllAccountQuery,
} from "store/apis/transactions";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { isSourceConnected } from "util/isSourceConnected";
import { ErrorItem } from "types/Models/banks";
import { BackendError } from "types/utils/error";
import { DateRangeValue, getDateRangeWithSetting } from "utils/getDateRange";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import { SourceFilter } from "types/Models/books";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Divider } from "components/design/Divider";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { CaretDown } from "components/icons/CaretDown";
import * as Yup from "yup";
import classNames from "classnames";
import { RightArrow } from "components/icons/RightArrow";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { baseDatePeriod } from "utils/getDatePeriod";
import { useLocation } from "react-router-dom";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { YYYY_MM_DD } from "constants/date";
import { BANK_CONNECTION_PROVIDER } from "constants/dataSource";
import ToolTip from "components/design/toolTip";
import { REFRESH_TYPE, RefreshType } from "constants/transaction";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";

type RefreshModalProps = {
  isOpen: boolean;
  onClose: () => void;
  type: RefreshType;
};

type FormValues = {
  period: string;
  sources: string[];
  from_date: string;
  to_date: string;
};

const validationSchema = Yup.object().shape({
  period: Yup.string().required("Period is required"),
  sources: Yup.array().min(1, "Source is required"),
  from_date: Yup.string().when("period", {
    is: DATE_PERIOD.CUSTOM,
    then: (schema) => schema.required("Start date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  to_date: Yup.string().when("period", {
    is: DATE_PERIOD.CUSTOM,
    then: (schema) => schema.required("End date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const SourceSelection = ({
  refreshType,
  isStripePage,
  froms,
  isLoading,
  isSuccess,
  connectionStatusByBrand,
}: {
  refreshType: string;
  isStripePage: Boolean;
  froms: SourceFilter[];
  isLoading: boolean;
  isSuccess: boolean;
  connectionStatusByBrand: Record<string, boolean>;
}) => {
  const { values, setFieldValue, errors, touched } =
    useTypedFormikContext<FormValues>();
  const [isOpen, setIsOpen] = useState(false);

  const sourceList =
    refreshType === REFRESH_TYPE.ATTACHMENTS
      ? froms.filter(
          (item) =>
            !item.is_manually_added &&
            (item.connection_provider === BANK_CONNECTION_PROVIDER.BREX ||
              item.connection_provider === BANK_CONNECTION_PROVIDER.MERCURY)
        )
      : froms.filter((item) => !item.is_manually_added);

  const selectFromIds = values.sources;

  const selectableSourceList = useMemo(
    () =>
      sourceList.filter((source) =>
        isSourceConnected(source, connectionStatusByBrand)
      ),
    [sourceList, connectionStatusByBrand]
  );

  const banksByName = useMemo(
    () =>
      sourceList.reduce((acc, cc) => {
        if (!acc[cc.brand_name]) {
          acc[cc.brand_name] = [];
        }
        acc[cc.brand_name].push(cc);
        return acc;
      }, {} as Record<string, SourceFilter[]>),
    [sourceList]
  );

  const selectedBanks = useMemo(() => {
    if (selectFromIds.length === 0) return [];
    return froms.filter((bank) => selectFromIds.includes(bank.uuid));
  }, [froms, selectFromIds]);

  const selectedBankNames = useMemo(() => {
    return selectedBanks
      .map((bank) =>
        bank.mask ? `${bank.nickname} - ${bank.mask}` : bank.nickname
      )
      .join(", ");
  }, [selectedBanks]);

  const onBankNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    const allbanks = new Set(
      banksByName[name]
        .filter((bank) => isSourceConnected(bank, connectionStatusByBrand))
        .map(({ uuid }) => uuid)
    );

    if (Array.isArray(selectFromIds)) {
      let newSelectedIds: Set<string>;

      if (checked) {
        newSelectedIds = new Set([...selectFromIds, ...allbanks]);
      } else {
        newSelectedIds = new Set(
          selectFromIds.filter((fromId) => !allbanks.has(fromId))
        );
      }

      setFieldValue(
        "sources",
        newSelectedIds.size > 0 ? Array.from(newSelectedIds) : []
      );
    }
  };

  const onHandleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    if (Array.isArray(selectFromIds)) {
      let newSelectedIds: Set<string> = new Set();
      if (checked) {
        newSelectedIds = new Set([...selectFromIds, value]);
      } else {
        newSelectedIds = new Set(
          [...selectFromIds]?.filter((fromId) => fromId !== value)
        );
      }
      setFieldValue(
        "sources",
        newSelectedIds.size > 0 ? [...newSelectedIds] : []
      );
    }
  };

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      setFieldValue("sources", []);
    } else {
      const allSourceIds = selectableSourceList.map((source) => source.uuid);
      setFieldValue("sources", allSourceIds);
    }
  };

  const handleClearAll = () => {
    setFieldValue("sources", []);
  };

  const selectedCount = selectFromIds.length;
  const firstBank = selectedBanks[0];
  const isAllSelected =
    selectableSourceList.length > 0 &&
    selectedCount === selectableSourceList.length;

  return (
    <div className="t-flex t-flex-col t-gap-2">
      <Label className="!t-pb-0" required>
        Source
      </Label>
      <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dropdown.Trigger asChild>
          <div className="all:unset t-box-border t-flex t-h-10 t-w-[100%] t-items-center t-justify-between t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-4 t-transition-all t-cursor-pointer">
            <div className="t-flex t-items-center t-gap-2 t-flex-1 t-min-w-0">
              <div
                className={classNames(
                  "t-overflow-hidden t-text-ellipsis t-whitespace-nowrap",
                  {
                    "t-text-body-sm t-text-text-100": selectedCount > 0,
                    "t-text-text-30 t-text-body-sm": selectedCount === 0,
                  }
                )}
              >
                {selectedCount > 0
                  ? firstBank?.mask
                    ? `${firstBank.nickname} - ${firstBank.mask}`
                    : firstBank?.nickname
                  : "Select Source"}
              </div>
              {selectedCount > 1 && (
                <ToolTip text={selectedBankNames}>
                  <div className="t-flex-shrink-0 t-rounded t-bg-surface-grey t-px-2 t-py-0.5 t-text-caption t-text-text-30">
                    +{selectedCount - 1}
                  </div>
                </ToolTip>
              )}
            </div>
            <span
              className={`t-transition-transform t-duration-200 ${
                isOpen ? "-t-rotate-180" : "t-rotate-0"
              }`}
            >
              <CaretDown />
            </span>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={6}
            align="start"
            className="t-w-[31rem] t-max-h-[25rem] t-overflow-auto"
          >
            <div className="t-flex t-flex-col t-gap-4 t-p-4">
              <Async.Root
                isLoading={isLoading}
                isSuccess={isSuccess}
                isEmpty={sourceList.length === 0}
                loaderType="secondary"
              >
                <Async.Empty>
                  <div className="t-text-center t-text-text-30">
                    No sources found
                  </div>
                </Async.Empty>
                <Async.Success>
                  <div className="t-flex t-flex-col t-gap-2">
                    <div className="t-flex t-items-center t-justify-between">
                      <Checkbox
                        name="select-all"
                        label={
                          <span className="t-text-text-60">Select all</span>
                        }
                        checked={isAllSelected}
                        onChange={handleSelectAllToggle}
                      />
                      <Button
                        customType="ghost"
                        size="small"
                        onClick={handleClearAll}
                        disabled={selectedCount === 0}
                      >
                        <div>Clear all</div>
                      </Button>
                    </div>
                  </div>
                  {Object.entries(banksByName).map(
                    ([name, banks], index, array) => {
                      const selectableBanks = banks.filter((bank) =>
                        isSourceConnected(bank, connectionStatusByBrand)
                      );
                      const isGroupDisabled = selectableBanks.length === 0;

                      const checked =
                        selectableBanks.length > 0 &&
                        selectableBanks.every((bank) =>
                          new Set(selectFromIds as string[]).has(bank.uuid)
                        );

                      const indeterminate =
                        selectableBanks.some((bank) =>
                          new Set(selectFromIds as string[]).has(bank.uuid)
                        ) && !checked;

                      const isLastItem = index === array.length - 1;

                      return (
                        <span className="t-flex t-flex-col t-gap-4" key={name}>
                          <div className="t-flex t-items-center t-gap-2">
                            <Checkbox
                              name={name}
                              label={
                                <span className="t-text-text-60">{name}</span>
                              }
                              value={name}
                              onChange={onBankNameChange}
                              checked={checked}
                              indeterminate={indeterminate}
                              disabled={isGroupDisabled}
                            />
                            {isGroupDisabled && (
                              <Tag tagType="gray" size="small" icon={false}>
                                Disconnected
                              </Tag>
                            )}
                          </div>
                          <span className="t-ml-6 t-flex t-flex-col t-gap-4">
                            {banks.map((from) => {
                              const isFromConnected = isSourceConnected(
                                from,
                                connectionStatusByBrand
                              );

                              return (
                                <Checkbox
                                  key={from.uuid}
                                  name={from?.uuid}
                                  label={
                                    <>
                                      {from?.nickname}
                                      {from?.mask && <> - {from?.mask}</>}
                                    </>
                                  }
                                  value={from?.uuid}
                                  checked={new Set(
                                    selectFromIds as string[]
                                  ).has(from?.uuid)}
                                  onChange={onHandleChange}
                                  disabled={!isFromConnected}
                                />
                              );
                            })}
                          </span>
                          {!isLastItem && <Divider color="grey" />}
                        </span>
                      );
                    }
                  )}
                </Async.Success>
              </Async.Root>
            </div>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {touched.sources && errors.sources && (
        <div className="t-text-body-sm t-leading-none t-text-red t-font-light t-italic">
          {errors.sources}
        </div>
      )}
    </div>
  );
};

const RefreshForm = ({
  refreshType,
  isStripePage,
  froms,
  isLoading,
  isSuccess,
  connectionStatusByBrand,
}: {
  refreshType: string;
  isStripePage: Boolean;
  froms: SourceFilter[];
  isLoading: boolean;
  isSuccess: boolean;
  connectionStatusByBrand: Record<string, boolean>;
}) => {
  const { values, setFieldValue } = useTypedFormikContext<FormValues>();

  const PERIOD_OPTIONS = [
    ...(refreshType === REFRESH_TYPE.TRANSACTIONS
      ? [{ label: "Since last sync", value: "since_last_sync" }]
      : []),
    ...baseDatePeriod,
    { label: "Custom", value: DATE_PERIOD.CUSTOM },
  ];

  const selectedPeriod = PERIOD_OPTIONS.find(
    (option) => option.value === values.period
  );

  const isCustomPeriod = values.period === DATE_PERIOD.CUSTOM;

  const handlePeriodChange = (value: string) => {
    if (value === DATE_PERIOD.CUSTOM && !values.to_date) {
      setFieldValue("to_date", dayjs().format(YYYY_MM_DD));
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-4">
      {isStripePage && (
        <div className="t-text-body-italic t-text-text-30 t-italic">
          Note: Refreshing Stripe transactions will also update the related
          Stripe invoices and create aggregate entries as needed.
        </div>
      )}
      <Combobox
        withForm
        name="period"
        label="Period"
        required
        menuPortalTarget={document.body}
        options={PERIOD_OPTIONS}
        value={selectedPeriod || null}
        isClearable={false}
        onChange={(option: any) => {
          if (option?.value) {
            handlePeriodChange(option.value);
          }
        }}
      />

      {isCustomPeriod && (
        <div>
          <Label required>Select Range</Label>
          <div className="t-flex t-justify-between t-flex-row t-gap-2 t-items-center">
            <div className="t-flex-1">
              <DateField
                name="from_date"
                placeholder="Select start date"
                customSize="small"
                showErrorOnceTouched
                portalId="refresh-start-0"
                required
                maxDate={values.to_date ? new Date(values.to_date) : undefined}
                rightComponent={
                  <span className="t-text-text-30">
                    <CalendarIcon />
                  </span>
                }
              />
            </div>

            <span className="t-text-text-30">
              <RightArrow />
            </span>

            <div className="t-flex-1">
              <DateField
                name="to_date"
                defaultValue={dayjs().format(YYYY_MM_DD)}
                placeholder="Select end date"
                customSize="small"
                showErrorOnceTouched
                portalId="refresh-end-0"
                maxDate={new Date()}
                minDate={
                  values.from_date ? new Date(values.from_date) : undefined
                }
                rightComponent={
                  <span className="t-text-text-30">
                    <CalendarIcon />
                  </span>
                }
              />
            </div>
          </div>
        </div>
      )}

      <SourceSelection
        refreshType={refreshType}
        isStripePage={isStripePage}
        froms={froms}
        isLoading={isLoading}
        isSuccess={isSuccess}
        connectionStatusByBrand={connectionStatusByBrand}
      />
    </div>
  );
};

export const RefreshModal = ({ isOpen, onClose, type }: RefreshModalProps) => {
  const { alertToast, info: infoToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { pathname } = useLocation();
  const [refreshTransactions, { isLoading: isRefreshingTransactions }] =
    useRefreshTransactionsMutation();
  const [refreshReceipts, { isLoading: isRefreshingReceipts }] =
    useRefreshReceiptsMutation();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;
  const isLoading = isRefreshingTransactions || isRefreshingReceipts;
  const isStripePage = pathname.includes("/stripe");

  const {
    data: froms = [],
    isLoading: isLoadingSources,
    isSuccess: isSourcesSuccess,
  } = useGetAllAccountQuery(
    {
      groupId,
      entityId,
      account_type: isStripePage ? "STRIPE" : "EXCLUDE_STRIPE",
    },
    { skip: !groupId || !entityId }
  );

  const { data: ledgerBanks } = useGetEntityBanksQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const connectionStatusByBrand = useMemo(() => {
    const itemsByBrand = (ledgerBanks?.items ?? []).reduce((acc, item) => {
      const brandName = item.bank_brand.name;
      acc[brandName] = [...(acc[brandName] ?? []), item];
      return acc;
    }, {} as Record<string, ErrorItem[]>);

    return Object.entries(itemsByBrand).reduce((acc, [brandName, items]) => {
      acc[brandName] = items.every((item) => item.is_bank_connected);
      return acc;
    }, {} as Record<string, boolean>);
  }, [ledgerBanks]);

  const handleSubmit = async (values: FormValues) => {
    try {
      let from_date: string;
      let to_date: string;

      if (
        values.period === DATE_PERIOD.CUSTOM ||
        values.period === "since_last_sync"
      ) {
        from_date = values.from_date
          ? dayjs(values.from_date).format(YYYY_MM_DD)
          : "";
        to_date = values.to_date
          ? dayjs(values.to_date).format(YYYY_MM_DD)
          : "";
      } else {
        const dateRange = getDateRangeWithSetting(
          values.period as DateRangeValue,
          bookkeepingSettings?.tax_year_end,
          bookkeepingStartDate
        );
        from_date = dayjs(dateRange.startDate).format(YYYY_MM_DD);
        to_date = dayjs(dateRange.endDate).format(YYYY_MM_DD);
      }

      if (type === REFRESH_TYPE.TRANSACTIONS) {
        await refreshTransactions({
          groupId,
          entityId,
          from_date,
          to_date,
          sources: values.sources,
        }).unwrap();

        infoToast({
          message: `Updating the transactions. We'll notify you when they're ready.`,
        });
      } else {
        await refreshReceipts({
          entityId,
          groupId,
          payload: {
            from_date,
            to_date,
            entity_bank_account_ids: values.sources,
          },
        }).unwrap();

        infoToast({
          message:
            "Updating the attachments & memos. We'll notify you when they're ready.",
        });
      }

      onClose();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay asChild>
        <FormikForm
          initialValues={{
            period: type === REFRESH_TYPE.TRANSACTIONS ? "since_last_sync" : "",
            sources: [],
            from_date: "",
            to_date: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {type === REFRESH_TYPE.TRANSACTIONS
                    ? "Refresh transactions"
                    : "Refresh attachments & memos"}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-2">
                <RefreshForm
                  refreshType={type}
                  isStripePage={isStripePage}
                  froms={froms}
                  isLoading={isLoadingSources}
                  isSuccess={isSourcesSuccess}
                  connectionStatusByBrand={connectionStatusByBrand}
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Button onClick={onClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  type="submit"
                  onClick={submitForm}
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  Confirm
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
